/**
 * Business logic hook for lesson completion workflow
 * Manages student's journey through a lesson: loading, answering, submitting
 * Uses React Query + reducer (no XState)
 */

import { useReducer, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLessonWithAnswers } from './useLessonsQueries';
import { useAnswerMutations } from './use-answer-mutations';
import { slideRegistry } from '../core/plugin-system';
import { validateAnswer, canSubmitLesson } from '../shared/validation';
import { getLessonTypeConfig } from '../config/lesson-types.config';
import { lessonsAPI } from '../api';
import type { AnswerDTO, SlideDTO } from '../core/types/lesson.types';
import type {
  LessonCompletionState,
  LessonCompletionAction,
} from '../core/types/workflow.types';
import { getErrorMessage, isApiValidationError } from '@/core/api/response-handler';
import { esteActivitatePracticaCuClase } from '../utils/clase-flag';
import { pentruServer } from '../utils/clase-canonice';
import { trackLessonCompleted, trackLessonStarted } from '@/telemetry/posthog-events';
import {
  ID_CAMP_PARTICIPANTI,
  esteNumarParticipantiValid,
} from '../features/completion/components/CampParticipanti';

// ============================================================================
// REDUCER FOR COMPLETION STATE
// ============================================================================

const initialState: LessonCompletionState = {
  status: 'idle',
  lesson: null,
  currentSlideIndex: 0,
  answers: [],
  lessonAnswerId: null,
  currentAnswer: null,
  progress: {
    totalSlides: 0,
    completedSlides: 0,
    currentSlideIndex: 0,
    percentComplete: 0,
    isCompleted: false,
  },
  isPreview: false,
  errors: {},
  answeredSlideIds: new Set(), // Track which slides have been answered
  isResumed: false, // Flag to track if lesson is being resumed vs fresh start
};

function lessonCompletionReducer(
  state: LessonCompletionState,
  action: LessonCompletionAction
): LessonCompletionState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, status: 'loading' };

    case 'LOAD_SUCCESS': {
      const { lesson, answers } = action.payload;

      // Sort slides by their 'number' field to ensure correct display order
      // Backend may return slides unsorted, causing VIDEO_PREVIEW (number: 0) to be skipped
      const sortedLesson = {
        ...lesson,
        slides: lesson.slides?.slice().sort((a, b) =>
          (a.number ?? 0) - (b.number ?? 0)
        )
      };

      const totalSlides = sortedLesson.slides?.length || 0;
      const completedSlides = answers.length;

      // ✅ FIX: Detect lesson state based on ANSWER COUNT (attemptState not available in getById)
      // - Has answers but not all slides → IN_PROGRESS (resume from where left off)
      // - Has all slides answered → COMPLETED (but backend might allow practice mode)
      // - No answers → Fresh start

      // Completion detection: All slides answered
      const isCompleted = completedSlides >= totalSlides && totalSlides > 0;

      // ✅ FIX: Resume detection based on answer count, not attemptState
      // Student has started lesson (has some answers) but hasn't finished all slides
      const isResumed = completedSlides > 0 && !isCompleted;

      // Build map of answered slide IDs for O(1) lookups (duplicate prevention)
      const answeredSlideIds = new Set(answers.map(a => a.slideId));

      // ⚠️ CRITICAL: Detect mismatch - answers loaded but Set is empty (loading bug)
      if (completedSlides > 0 && answeredSlideIds.size === 0) {
        // Force reload to fix state inconsistency
        window.location.reload();
        return state;
      }

      // ✅ FIX: Resume from first unanswered slide (answer count = slide index)
      // - No answers (completedSlides = 0) → Start at slide 0
      // - 3 answers (completedSlides = 3) → Resume at slide 3 (0-indexed)
      // - Completed → Start at 0 (will show completion screen)
      const initialSlideIndex = isCompleted ? 0 : completedSlides;

      return {
        ...state,
        status: isCompleted ? 'completed' : 'ready',
        lesson: sortedLesson,
        answers: answers,
        currentSlideIndex: initialSlideIndex,
        progress: {
          totalSlides,
          completedSlides,
          currentSlideIndex: initialSlideIndex,
          percentComplete: totalSlides > 0 ? (completedSlides / totalSlides) * 100 : 0,
          isCompleted,
        },
        answeredSlideIds, // Store answered slide IDs for duplicate prevention
        isResumed, // ✅ Flag indicates this is a resumed lesson (has progress but not finished)
      };
    }

    case 'LOAD_ERROR':
      return { ...state, status: 'error', errors: { _load: action.payload } };

    case 'START_LESSON': {
      // If resuming (has progress), preserve currentSlideIndex from LOAD_SUCCESS
      // If fresh start, set to 0
      const startIndex = state.isResumed ? state.currentSlideIndex : 0;
      const startSlide = state.lesson?.slides?.[startIndex];

      return {
        ...state,
        status: 'in_progress',
        currentSlideIndex: startIndex,
        currentAnswer: startSlide ? { slideId: startSlide.id } : null,
        progress: {
          ...state.progress,
          currentSlideIndex: startIndex,
        },
      };
    }

    case 'UPDATE_CURRENT_ANSWER':
      return {
        ...state,
        currentAnswer: state.currentAnswer
          ? { ...state.currentAnswer, ...action.payload }
          : action.payload,
      };

    case 'SUBMIT_ANSWER_START':
      return { ...state, status: 'submitting' };

    case 'SUBMIT_ANSWER_SUCCESS': {
      const newAnswers = [...state.answers, action.payload];
      const completedSlides = newAnswers.length;
      const totalSlides = state.lesson?.slides?.length || 0;
      const allSlidesAnswered = completedSlides >= totalSlides;

      // ✅ FIX: For CLASS_EXERCISE, don't mark as completed until participants slide submitted
      // Participants slide is virtual and submitted via COMPLETE_LESSON action
      const isClassExercise = state.lesson?.type === 'CLASS_EXERCISE';
      const isCompleted = allSlidesAnswered && !isClassExercise;

      // Update answered slide IDs with the newly submitted answer
      const updatedAnsweredSlideIds = new Set(state.answeredSlideIds);
      updatedAnsweredSlideIds.add(action.payload.slideId);

      // ✅ Store lessonAnswerId when lesson is completed (backend creates LessonAnswer aggregate)
      const lessonAnswerId = action.payload.isLessonCompletedNow && action.payload.lessonAnswerId
        ? action.payload.lessonAnswerId
        : state.lessonAnswerId;

      return {
        ...state,
        status: isCompleted ? 'completed' : 'in_progress',
        answers: newAnswers,
        lessonAnswerId,
        currentAnswer: null,
        answeredSlideIds: updatedAnsweredSlideIds,
        progress: {
          ...state.progress,
          completedSlides,
          percentComplete: totalSlides > 0 ? (completedSlides / totalSlides) * 100 : 0,
          isCompleted,
        },
      };
    }

    case 'SUBMIT_ANSWER_ERROR':
      return {
        ...state,
        status: 'in_progress',
        errors: { _submit: action.payload },
      };

    case 'NEXT_SLIDE': {
      // For CLASS_EXERCISE, allow advancing to virtual participants slide (slides.length)
      // For other lessons, cap at last real slide (slides.length - 1)
      const isClassExercise = state.lesson?.type === 'CLASS_EXERCISE';
      // 🔴 SP-05a: activitatea practica primeste si ea un index virtual in plus — ecranul
      //    „care clase", ultimul pas, de pe care pleaca trimiterea. Conditia e pe TIP, deci
      //    pentru LESSON / HOMEWORK / MONTHLY_CHALLENGE plafonul ramane exact cel de azi.
      // Trece prin comutator: cu flagul oprit e false, deci plafonul ramane cel de azi.
      const isPracticalActivity = esteActivitatePracticaCuClase(state.lesson?.type);
      const numarSlideuriReale = state.lesson?.slides?.length || 0;
      const maxIndex = isClassExercise || isPracticalActivity
        ? numarSlideuriReale  // Allow one extra index for the virtual slide
        : numarSlideuriReale - 1;  // Cap at last real slide

      const nextIndex = Math.min(
        state.currentSlideIndex + 1,
        maxIndex
      );

      /**
       * 🔴 Trecerea pe ecranul de clase PASTREAZA raspunsul ultimului slide real.
       *
       * De ce: la activitatea practica, `clase` pleaca in ACEEASI cerere ca raspunsul —
       * deci raspunsul nu se trimite pe ultimul slide, ci pe ecranul de clase. Daca l-am
       * sterge aici, ca la orice alta trecere, cererea ar pleca fara pozele omului: nicio
       * eroare, ecran identic cu unul care merge, munca pierduta.
       *
       * ⚠️ Conditia e pe TRECEREA ANUME (nextIndex === numarSlideuriReale), nu pe tipul de
       * lectie. Pastrat la fiecare trecere, raspunsul unui slide ar ajunge pe urmatorul, si
       * activitatea practica are slide-uri de incarcat poze.
       */
      const treceLaEcranulDeClase = isPracticalActivity && nextIndex === numarSlideuriReale;

      return {
        ...state,
        currentSlideIndex: nextIndex,
        currentAnswer: treceLaEcranulDeClase ? state.currentAnswer : null,
        progress: { ...state.progress, currentSlideIndex: nextIndex },
      };
    }

    case 'PREVIOUS_SLIDE': {
      const prevIndex = Math.max(state.currentSlideIndex - 1, 0);
      return {
        ...state,
        currentSlideIndex: prevIndex,
        currentAnswer: null,
        progress: { ...state.progress, currentSlideIndex: prevIndex },
      };
    }

    case 'GO_TO_SLIDE':
      return {
        ...state,
        currentSlideIndex: action.payload,
        currentAnswer: null,
        progress: { ...state.progress, currentSlideIndex: action.payload },
      };

    case 'COMPLETE_LESSON':
      return { ...state, status: 'completed' };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Hook for lesson completion workflow
 * Manages student progress through slides and answer submission
 *
 * @param lessonId - ID of the lesson to complete
 * @param userId - ID of the user completing the lesson
 * @param isPreview - Whether this is a preview (no saving)
 *
 * @example
 * const completion = useLessonCompletion('lesson-123', 'user-456');
 * completion.start();
 * completion.updateAnswer({ selectedOptionsIds: ['opt1'] });
 * completion.submitCurrentAnswer();
 */
export function useLessonCompletion(
  lessonId: string,
  userId: string | undefined,
  isPreview = false
) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(lessonCompletionReducer, {
    ...initialState,
    isPreview,
  });

  // Debounce guard to prevent duplicate slide advance requests
  const [isAdvancingSlide, setIsAdvancingSlide] = useState(false);

  // Participants count for CLASS_EXERCISE lessons. Porneste GOL (null), nu de la 1: `1` era exact
  // valoarea cu care raspunsul ajungea la admin cand profesorul nu apuca sa scrie numarul.
  const [participants, setParticipants] = useState<number | null>(null);
  /** S-a apasat „Trimite" pe pagina cu pozele fara un numar valid de participanti. */
  const [participantiIncercat, setParticipantiIncercat] = useState(false);

  /**
   * Textul BRUT al claselor (SP-05a), exact cum l-a scris omul — asta pleaca la server, nu
   * canonicele. Normalizarea din frontend e doar oglinda de pe ecran.
   */
  const [clase, setClase] = useState<string>('');
  /**
   * S-a cerut deja confirmarea pentru camp gol? Campul e OPTIONAL (fara clase activitatea ia
   * tot 50 de puncte), deci nu se blocheaza — se intreaba **o singura data**, si atat.
   */
  const [golConfirmat, setGolConfirmat] = useState<boolean>(false);
  /**
   * Fragmentele pe care SERVERUL nu le-a inteles (400). Vin de la el, nu din normalizatorul local:
   * cine respinge e tot cine decide. Se golesc la fiecare incercare noua, ca sa nu ramana pe ecran
   * o eroare despre un text pe care omul tocmai l-a schimbat.
   */
  const [fragmenteRespinse, setFragmenteRespinse] = useState<string[]>([]);
  /**
   * Clasele raportate de server ca DEJA PUNCTATE, din raspunsul de 201.
   *
   * ⚠️ AVERTIZARE, nu refuz: trimiterea a reusit deja cand omul o vede. Si e o FOTOGRAFIE, nu o
   * promisiune — adevarul se stabileste la aprobare, iar intre timp altcineva poate aproba sau
   * respinge. De aia textul spune ce E, nu ce VA FI (formularea lui B1: „va fi respinsa" ne-ar
   * face mincinosi cand starea se muta).
   */
  const [claseDejaPunctate, setClaseDejaPunctate] = useState<string[]>([]);
  /* A trimis omul CHIAR ACUM, in montarea asta? `CompletionScreen` se randeaza si la
     reintrarea pe o lectie deja completata, iar avertizarea e despre trimiterea
     curenta — fara santinela, ar vorbi saptamana viitoare despre ceva ce omul nu
     mai poate schimba. Azi lista e goala la o montare noua oricum, dar asta e un
     accident al implementarii, nu o regula: cine o va citi candva din `lessonAnswer`
     (date de la server, prezente si la reintrare) ar reaprinde avertizarea singur. */
  const [aTrimisAcum, setATrimisAcum] = useState(false);

  // React Query hooks
  // In preview mode, skip loading answers (only load lesson)
  const { lesson, answers, isLoading, isError, error } = useLessonWithAnswers(
    lessonId,
    userId,
    { enabled: true, skipAnswers: isPreview }
  );
  const answerMutations = useAnswerMutations();

  // Memoize answers to prevent creating new array references on every render
  // This prevents infinite loop in useEffect when answers is undefined
  const stableAnswers = useMemo(() => answers || [], [answers]);

  // Lesson type configuration
  const config = lesson && lesson.type ? getLessonTypeConfig(lesson.type as any) : null;

  // Check if this is a CLASS_EXERCISE lesson (requires participants slide)
  const isClassExercise = useMemo(() => {
    return state.lesson?.type === 'CLASS_EXERCISE';
  }, [state.lesson?.type]);

  /**
   * Activitatea practica (SP-05a): are un ecran virtual „care clase" ca ULTIM pas, de pe care
   * pleaca trimiterea.
   *
   * ⚠️ NU e acelasi tipar cu slide-ul de participanti, desi arata la fel. Acela apare DUPA ce
   * raspunsul a fost trimis si face o A DOUA scriere, pe `lessonAnswerId` intors de server
   * (`updateParticipants`). Aici `clase` face parte din aceeasi cerere ca raspunsul, deci
   * ecranul e INAINTE de trimitere. Copiat mecanic, tiparul ar fi cerut o ruta inexistenta.
   */
  const isPracticalActivity = useMemo(() => {
    // 🔴 Sub comutator (`VITE_PAS_CLASE_ACTIVITATE`). Oprit ⇒ false, deci TOATE cele cinci
    //    puncte atinse din hook cad pe calea de azi — inclusiv momentul trimiterii, nu doar
    //    ecranul. Livrarea se desparte de expunere, ca la SP-01 si SP-03.
    return esteActivitatePracticaCuClase(state.lesson?.type);
  }, [state.lesson?.type]);

  // Aceeasi regula ca `LessonPlayer.isPracticeMode`: reluarea unei lectii deja aprobate/completate.
  const attemptState = (state.lesson as { attemptState?: string } | null)?.attemptState;
  const isPracticeMode = !isPreview && (attemptState === 'APPROVED' || attemptState === 'COMPLETED');

  /* `lesson_completed` dintr-un SINGUR loc: tranzitia spre `completed` a unei parcurgeri inceputa
     in montarea asta. `COMPLETE_LESSON` se trimite din mai multe ramuri, iar `SUBMIT_ANSWER_SUCCESS`
     poate pune deja `completed` inaintea lui — santinela face din ele un singur eveniment. O lectie
     incarcata direct ca `completed` (reintrare) nu trece prin `in_progress`, deci nu raporteaza. */
  const parcurgereInCurs = useRef(false);
  useEffect(() => {
    if (isPreview || !state.lesson) return;
    if (state.status === 'in_progress' || state.status === 'submitting') {
      parcurgereInCurs.current = true;
    } else if (state.status === 'completed' && parcurgereInCurs.current) {
      parcurgereInCurs.current = false;
      trackLessonCompleted({
        lessonType: state.lesson.type,
        lessonId: state.lesson.id,
        isPracticeMode,
        slideCount: state.lesson.slides?.length,
      });
    }
  }, [state.status, state.lesson, isPreview, isPracticeMode]);

  // ========================================================================
  // INITIALIZATION
  // ========================================================================

  useEffect(() => {
    if (lesson) {
      dispatch({
        type: 'LOAD_SUCCESS',
        payload: { lesson, answers: stableAnswers },
      });
    }
  }, [lesson, stableAnswers]);

  useEffect(() => {
    if (isError && error) {
      dispatch({ type: 'LOAD_ERROR', payload: error.message });
    }
  }, [isError, error]);

  // ========================================================================
  // COMPUTED VALUES
  // ========================================================================

  const currentSlide: SlideDTO | null = useMemo(() => {
    if (!state.lesson?.slides || state.currentSlideIndex < 0) {
      return null;
    }
    // Clamp index to valid range to prevent accessing undefined array indices
    const safeIndex = Math.min(state.currentSlideIndex, state.lesson.slides.length - 1);
    return state.lesson.slides[safeIndex] || null;
  }, [state.lesson, state.currentSlideIndex]);

  const currentPlugin = useMemo(() => {
    if (!currentSlide || !currentSlide.type) return null;
    return slideRegistry.get(currentSlide.type);
  }, [currentSlide]);

  // Check if we're on the virtual participants slide (CLASS_EXERCISE only)
  const isParticipantsSlide = useMemo(() => {
    if (!isClassExercise) return false;
    const totalRealSlides = state.lesson?.slides?.length || 0;
    // Participants slide appears when index = totalRealSlides (one past the last real slide)
    return state.currentSlideIndex === totalRealSlides;
  }, [isClassExercise, state.currentSlideIndex, state.lesson]);

  /**
   * Ultimul slide REAL al unui CLASS_EXERCISE — pagina cu pozele, unde se cere si numarul de
   * participanti (din 2026-10-22), ca sa plece odata cu raspunsul, nu pe un ecran ulterior.
   */
  const isLastRealSlide = useMemo(() => {
    if (!isClassExercise) return false;
    return state.currentSlideIndex === (state.lesson?.slides?.length || 0) - 1;
  }, [isClassExercise, state.currentSlideIndex, state.lesson]);

  /**
   * Campul de participanti se cere pe pagina cu pozele — dar NU in modul exersare: acolo
   * raspunsul nu se salveaza (raspuns efemer), deci n-ar avea unde sa ajunga numarul.
   */
  const campParticipantiPePagina = isLastRealSlide && !isPracticeMode;

  /** Ecranul virtual „care clase" (SP-05a), un index dupa ultimul slide real. */
  const isClaseSlide = useMemo(() => {
    if (!isPracticalActivity) return false;
    return state.currentSlideIndex === (state.lesson?.slides?.length || 0);
  }, [isPracticalActivity, state.currentSlideIndex, state.lesson]);

  const isLastSlide = useMemo(() => {
    const totalSlides = state.lesson?.slides?.length || 0;
    // For CLASS_EXERCISE, the participants slide is after the last real slide
    // So the last real slide is NOT the final slide (participants slide is)
    if (isClassExercise) {
      return false; // Real slides are never "last" for CLASS_EXERCISE
    }
    // 🔴 SP-05a, acelasi rationament pentru activitatea practica: ultimul PAS e ecranul de
    //    clase, nu ultimul slide real. Fara linia asta, ultimul slide real ar trimite si ar
    //    incheia lectia inainte ca omul sa apuce sa scrie clasele.
    //    ⚠️ Conditia e pe tip: pentru LESSON / HOMEWORK / MONTHLY_CHALLENGE ramane exact
    //    comparatia de mai jos, neatinsa.
    if (isPracticalActivity) {
      return state.currentSlideIndex === totalSlides;
    }
    return state.currentSlideIndex === totalSlides - 1;
  }, [state.currentSlideIndex, state.lesson, isClassExercise, isPracticalActivity]);

  const isFirstSlide = useMemo(() => {
    return state.currentSlideIndex === 0;
  }, [state.currentSlideIndex]);

  const canGoNext = useMemo(() => {
    // For CLASS_EXERCISE, can advance until we reach participants slide
    if (isClassExercise) {
      return !isParticipantsSlide;
    }
    // SP-05a: la fel pentru activitatea practica — se avanseaza pana pe ecranul de clase.
    if (isPracticalActivity) {
      return !isClaseSlide;
    }
    return !isLastSlide;
  }, [isLastSlide, isClassExercise, isParticipantsSlide, isPracticalActivity, isClaseSlide]);

  const canGoPrevious = useMemo(() => {
    // In preview mode, can always go back
    if (isPreview) return !isFirstSlide;

    // In normal mode, can only go back if multiple attempts allowed
    return !isFirstSlide && (config?.features.allowsMultipleAttempts || false);
  }, [isFirstSlide, isPreview, config]);

  // ========================================================================
  // ANSWER VALIDATION
  // ========================================================================

  const validateCurrentAnswer = useCallback(() => {
    if (!currentSlide || !state.currentAnswer) {
      return { valid: false, errors: ['Răspunsul este incomplet'], canSubmit: false };
    }

    return validateAnswer(state.currentAnswer, currentSlide);
  }, [currentSlide, state.currentAnswer]);

  const canSubmitCurrentAnswer = useMemo(() => {
    const validation = validateCurrentAnswer();
    return validation.canSubmit;
  }, [validateCurrentAnswer]);

  // ========================================================================
  // WORKFLOW OPERATIONS
  // ========================================================================

  const start = useCallback(() => {
    if (state.status === 'ready') {
      dispatch({ type: 'START_LESSON' });
      if (!isPreview) {
        trackLessonStarted({
          lessonType: state.lesson?.type,
          lessonId: state.lesson?.id,
          isPracticeMode,
          isResumed: state.isResumed,
        });
      }
    }
  }, [state.status, state.lesson, state.isResumed, isPreview, isPracticeMode]);

  const updateAnswer = useCallback((updates: Partial<AnswerDTO>) => {
    dispatch({ type: 'UPDATE_CURRENT_ANSWER', payload: updates });
  }, []);

  const submitCurrentAnswer = useCallback(async () => {
    // ✅ LAYER 2: Function-level guard - prevents rapid double-submissions
    // This guard runs BEFORE any other logic to catch fast double-clicks
    if (state.status === 'submitting') {
      return;
    }

    // ✅ SPECIAL HANDLING: Participants slide for CLASS_EXERCISE
    if (isParticipantsSlide) {
      // Validarea si mesajul inline le face `ParticipantsSlide`; garda ramane pentru tipuri.
      if (!esteNumarParticipantiValid(participants)) {
        return;
      }

      // ✅ Get the LessonAnswer ID from state
      // Backend returned this when isLessonCompletedNow: true after last slide submission
      if (!state.lessonAnswerId) {
        toast.error('Eroare: Nu s-a găsit ID-ul lecției completate');
        return;
      }

      // Show loading state
      dispatch({ type: 'SUBMIT_ANSWER_START' });

      // ✅ Update participants count via API with correct LessonAnswer.id
      lessonsAPI.updateParticipants(state.lessonAnswerId, participants)
        .then(() => {
          // Success - complete the lesson
          toast.success(`Trimis spre aprobare (${participants} participanți)`, { role: 'status' });
          dispatch({ type: 'COMPLETE_LESSON' });
        })
        .catch((error) => {
          // Error - show message and stay on participants slide
          toast.error('Eroare la salvarea numărului de participanți');
          dispatch({ type: 'SUBMIT_ANSWER_ERROR', payload: getErrorMessage(error, 'Unknown error') });
        });

      return;
    }

    /**
     * ✅ SP-05a: ecranul „care clase" — ultimul pas al activitatii practice.
     *
     * Aici NU exista `currentSlide` (indexul e virtual), iar raspunsul din stare e cel al
     * ultimului slide REAL, pastrat deliberat la trecere. Cererea pleaca o singura data, cu
     * `clase` inauntru: de asta ultimul slide real nu mai trimite singur.
     */
    if (isClaseSlide) {
      const slideuri = state.lesson?.slides || [];
      const ultimulSlideReal = slideuri[slideuri.length - 1];

      if (!ultimulSlideReal || !state.currentAnswer || !state.lesson) {
        toast.error('Nu există răspuns de trimis');
        return;
      }

      if (!userId) {
        toast.error('Eroare: Utilizator neidentificat. Te rugăm să reîncarci pagina.');
        return;
      }

      // Camp gol: se intreaba O SINGURA data si se trimite oricum la a doua apasare.
      // Fara cifre si fara aritmetica pe ecran (directia S1): o intrebare devine negociere
      // in clipa in care spui cat platesti pentru raspuns.
      if (clase.trim() === '' && !golConfirmat) {
        setGolConfirmat(true);
        toast.warning('N-ai scris nicio clasă. Apasă din nou ca să trimiți așa.');
        return;
      }

      if (isPreview) {
        dispatch({
          type: 'SUBMIT_ANSWER_SUCCESS',
          payload: {
            ...state.currentAnswer,
            id: `preview-${Date.now()}`,
            slideId: ultimulSlideReal.id,
            lessonId: state.lesson.id,
            userId,
          } as AnswerDTO,
        });
        dispatch({ type: 'COMPLETE_LESSON' });
        return;
      }

      dispatch({ type: 'SUBMIT_ANSWER_START' });
      // Incercare noua ⇒ ecranul nu mai poarta rezultatul celei vechi.
      setFragmenteRespinse([]);
      setClaseDejaPunctate([]);
      try {
        const raspuns: AnswerDTO = {
          ...state.currentAnswer,
          slideId: ultimulSlideReal.id,
          lessonId: state.lesson.id,
          userId,
          // Textul brut, nu canonicele: serverul normalizeaza, si el are ultimul cuvant.
          // `pentruServer` plieaza sedila la virgula-jos: serverul accepta azi doar `ș`
          // (U+0219), iar cele doua caractere sunt indistinctibile pe ecran. Fara ea, un text
          // lipit dintr-un document vechi trecea de oglinda si pica cu 400 pe TOT sirul.
          ...(clase.trim() !== '' ? { clase: pentruServer(clase.trim()) } : {}),
        } as AnswerDTO;

        const salvat = await answerMutations.submit.mutateAsync(raspuns);
        dispatch({ type: 'SUBMIT_ANSWER_SUCCESS', payload: salvat });
        // Avertizarea din 201. Absenta si lista goala inseamna acelasi lucru: nimic de spus.
        // ⚠️ Cu punctarea pe clase oprita pe server, campul e MEREU gol — deci „nu apare nimic"
        //    nu inseamna „nu merge" (avertismentul lui B1).
        if (Array.isArray(salvat.claseDejaPunctate) && salvat.claseDejaPunctate.length > 0) {
          setClaseDejaPunctate(salvat.claseDejaPunctate);
        }
        // Se ridica DUPA trimiterea reusita, si numai aici.
        setATrimisAcum(true);
        // Confirmarea de camp gol n-are ce sa mai confirme dupa o trimitere reusita.
        toast.dismiss();
        // ⚠️ „Trimis spre aprobare", niciodata „inregistrate" sau „punctate": ruta intoarce
        //    201 si pentru o clasa deja punctata, iar 409-ul cu duplicatele apare abia la
        //    aprobare, in fata adminului (masurat pe DEV).
        // 🔴 SP-05a, valul (d), R2: `role: 'status'` in loc de `alert`-ul implicit al lui
        //    react-toastify. Masurat de S1: dupa 201 traiesc simultan doua regiuni live —
        //    avertizarea cu clasele deja punctate (`role="status"`, politicoasa) si toastul asta
        //    (`role="alert"`, asertiv). Asertivul INTRERUPE politicosul, deci exact informatia
        //    noua — clasele deja punctate — e cea care risca sa nu fie auzita.
        //    ⚠️ Nu-l fac mut (`role: 'none'`): «Trimis spre aprobare» ar ramane atunci doar text
        //    randat, iar o schimbare de DOM fara regiune live nu se anunta. Doua regiuni
        //    politicoase se pun la coada; una politicoasa si una asertiva se calca.
        toast.success('Trimis spre aprobare', { role: 'status' });
        dispatch({ type: 'COMPLETE_LESSON' });
      } catch (error) {
        // 🔴 Fragmentele numite de server ajung pe ecran, langa camp. Pana acum prop-ul exista,
        //    era documentat si tipat — si nu-l pasa NIMENI, deci omul vedea mesajul brut in loc
        //    de fragmentul lui. Al saselea caz din familia „pare ca ruleaza si nu ruleaza",
        //    gasit de S1 masurand fluxul (2026-09-06).
        if (isApiValidationError(error) && error.fragmenteNerecunoscute?.length) {
          setFragmenteRespinse(error.fragmenteNerecunoscute);
        }
        dispatch({
          type: 'SUBMIT_ANSWER_ERROR',
          payload: getErrorMessage(error, 'Trimiterea a eșuat'),
        });
      }
      return;
    }

    if (!currentSlide || !state.currentAnswer || !state.lesson) {
      toast.error('Nu există răspuns de trimis');
      return;
    }

    // ✅ CLIENT-SIDE DUPLICATE GUARD: Check if slide already answered
    // Prevents unnecessary API calls and race conditions
    if (currentSlide.id && state.answeredSlideIds.has(currentSlide.id)) {
      // Show warning and auto-advance to next unanswered slide
      toast.warning('Această întrebare a fost deja răspunsă. Trecem la următoarea.');

      if (!isLastSlide && !isAdvancingSlide) {
        setIsAdvancingSlide(true);
        setTimeout(() => {
          dispatch({ type: 'NEXT_SLIDE' });
          setTimeout(() => setIsAdvancingSlide(false), 100);
        }, 800);
      } else if (isLastSlide) {
        // All slides answered, complete lesson
        dispatch({ type: 'COMPLETE_LESSON' });
      }
      return;
    }

    // Validate before submit
    const validation = validateCurrentAnswer();
    if (!validation.canSubmit) {
      toast.error(validation.errors[0] || 'Răspunsul este invalid');
      return;
    }

    // ✅ CRITICAL: Validate userId exists before submitting to backend
    // Backend requires userId field - submission will fail with 400 if missing
    if (!userId) {
      toast.error('Eroare: Utilizator neidentificat. Te rugăm să reîncarci pagina.');
      return;
    }

    /**
     * 🔴 SP-05a: pe ULTIMUL slide real al activitatii practice nu se trimite inca.
     *
     * `clase` face parte din aceeasi cerere, si ele se scriu pe ecranul urmator. Daca am
     * trimite aici, ar pleca o cerere fara clase, iar a doua (de pe ecranul de clase) ar fi
     * un al doilea raspuns pentru acelasi slide. Se avanseaza, iar `NEXT_SLIDE` pastreaza
     * raspunsul exact pentru trecerea asta.
     *
     * ⚠️ Vine DUPA validare si dupa verificarea de `userId`, deci pozele raman obligatorii
     * ca azi — se schimba doar MOMENTUL trimiterii, nu conditiile ei.
     */
    if (isPracticalActivity && !isPreview && state.currentSlideIndex === (state.lesson?.slides?.length || 0) - 1) {
      dispatch({ type: 'NEXT_SLIDE' });
      return;
    }

    /**
     * 🔴 CLASS_EXERCISE: numarul de participanti e pe pagina cu pozele si e OBLIGATORIU inainte
     * de trimitere (sesizarea ECOTIC, 2026-10-22). Fara el, `POST /api/answers` ar crea raspunsul
     * cu `participants = 1` — implicitul din `LessonAnswer` — si asa ar ajunge la admin.
     *
     * Vine DUPA validarea pozelor, deci mesajul despre poze (daca lipsesc) are prioritate.
     * Eroarea e inline, sub camp; focusul o duce pe ecran si o anunta.
     */
    if (campParticipantiPePagina && !esteNumarParticipantiValid(participants)) {
      setParticipantiIncercat(true);
      document.getElementById(ID_CAMP_PARTICIPANTI)?.focus();
      return;
    }

    // Don't actually submit in preview mode
    if (isPreview) {
      dispatch({
        type: 'SUBMIT_ANSWER_SUCCESS',
        payload: {
          ...state.currentAnswer,
          id: `preview-${Date.now()}`,
          slideId: currentSlide.id,
          lessonId: state.lesson.id,
          userId,
        } as AnswerDTO,
      });

      // CLASS_EXERCISE: numarul s-a scris deja pe pagina asta, deci previzualizarea se incheie
      // aici, fara ecranul separat (care e doar plasa pentru un PATCH esuat).
      if (isLastRealSlide) {
        dispatch({ type: 'COMPLETE_LESSON' });
      } else if (!isLastSlide) {
        // Auto-advance to next slide if not last
        dispatch({ type: 'NEXT_SLIDE' });
      }
      return;
    }

    dispatch({ type: 'SUBMIT_ANSWER_START' });

    try {
      const answerToSubmit: AnswerDTO = {
        ...state.currentAnswer,
        slideId: currentSlide.id,
        lessonId: state.lesson.id,
        userId,
      } as AnswerDTO;

      const savedAnswer = await answerMutations.submit.mutateAsync(answerToSubmit);

      // ✅ PATTERN: Type guard for ephemeral response detection
      const isEphemeral = savedAnswer.punctajFinal === null &&
                          savedAnswer.isLessonCompletedNow === null;

      if (isEphemeral) {
        // Practice mode - show different feedback
        toast.info('🔄 Mod exersare - răspunsul nu este salvat', {
          autoClose: 3000
        });

        dispatch({ type: 'SUBMIT_ANSWER_SUCCESS', payload: savedAnswer });

        // Auto-advance to next slide.
        // ⚠️ `!isLastRealSlide`: la CLASS_EXERCISE `isLastSlide` e mereu fals, deci exersarea
        // trecea de pe ultima pagina pe ecranul de participanti — unde, fara `lessonAnswerId`
        // (raspunsul efemer nu-l are), ramanea blocata pe „Nu s-a găsit ID-ul lecției completate".
        if (!isLastSlide && !isLastRealSlide && !isAdvancingSlide) {
          setIsAdvancingSlide(true);
          setTimeout(() => {
            dispatch({ type: 'NEXT_SLIDE' });
            setTimeout(() => setIsAdvancingSlide(false), 100);
          }, 500);
        } else {
          toast.info('✅ Exersare completată! (fără puncte)');
          dispatch({ type: 'COMPLETE_LESSON' });
        }
        return; // Exit early - skip normal flow
      }

      /**
       * 🔴 CLASS_EXERCISE: numarul de participanti pleaca IMEDIAT dupa raspuns, in acelasi gest.
       *
       * Backendul creeaza `LessonAnswer` cu `participants = 1` si nu citeste campul din
       * `AnswerDTO`, deci a doua scriere (`PATCH .../participants`) ramane necesara — dar acum
       * o face trimiterea, nu un ecran pe care profesorul il poate parasi. Starea e inca
       * `submitting` pe durata ei, deci butonul ramane blocat.
       *
       * ⚠️ Daca `PATCH`-ul pica sau `lessonAnswerId` lipseste: raspunsul E salvat, deci nu se
       * retrimit pozele — se trece pe `ParticipantsSlide`, precompletat, de unde se reincearca
       * doar numarul.
       */
      if (savedAnswer.isLessonCompletedNow === true && isClassExercise) {
        let participantiSalvati = false;
        if (savedAnswer.lessonAnswerId && esteNumarParticipantiValid(participants)) {
          try {
            await lessonsAPI.updateParticipants(savedAnswer.lessonAnswerId, participants);
            participantiSalvati = true;
          } catch {
            toast.error('Fotografiile au fost trimise, dar numărul de participanți nu s-a salvat. Încearcă din nou.');
          }
        }

        dispatch({ type: 'SUBMIT_ANSWER_SUCCESS', payload: savedAnswer });
        if (participantiSalvati) {
          // Acelasi rol ca la activitatea practica: `status`, nu `alert` (vezi mai sus).
          toast.success(`Trimis spre aprobare (${participants} participanți)`, { role: 'status' });
          dispatch({ type: 'COMPLETE_LESSON' });
        } else {
          dispatch({ type: 'NEXT_SLIDE' });
        }
        return;
      }

      // Normal mode - proceed with existing logic
      dispatch({ type: 'SUBMIT_ANSWER_SUCCESS', payload: savedAnswer });

      // ✅ FIX: Check if backend marked lesson as complete
      // Backend returns isLessonCompletedNow: true when all slides are answered
      if (savedAnswer.isLessonCompletedNow === true) {
        // Other lesson types: complete immediately
        toast.success('Lecție completată cu succes!');
        dispatch({ type: 'COMPLETE_LESSON' });
        return; // Exit early
      }

      // Auto-advance to next slide if not last
      if (!isLastSlide && !isAdvancingSlide) {
        setIsAdvancingSlide(true);
        setTimeout(() => {
          dispatch({ type: 'NEXT_SLIDE' });
          setTimeout(() => setIsAdvancingSlide(false), 100);
        }, 500);
      } else if (isClassExercise && isLastRealSlide) {
        // For CLASS_EXERCISE, advance to participants slide instead of completing
        setIsAdvancingSlide(true);
        setTimeout(() => {
          dispatch({ type: 'NEXT_SLIDE' });
          setTimeout(() => setIsAdvancingSlide(false), 100);
        }, 500);
      } else if (isLastSlide) {
        // Last slide completed (non-CLASS_EXERCISE)
        toast.success('Lecție completată cu succes!');
        dispatch({ type: 'COMPLETE_LESSON' });
      }
    } catch (error: unknown) {
      const errorMsg = getErrorMessage(error, '');

      // ✅ HANDLE DUPLICATE ERROR: Treat as success (answer already exists on backend)
      // This handles race conditions where multiple tabs/requests submitted the same answer
      if (errorMsg.includes('duplicate key') ||
          errorMsg.includes('DataIntegrityViolationException') ||
          errorMsg.toLowerCase().includes('unique constraint')) {

        // Add to local state with placeholder (answer already saved on backend)
        const placeholderAnswer: AnswerDTO = {
          ...state.currentAnswer,
          slideId: currentSlide.id,
          lessonId: state.lesson.id,
          userId,
          id: `dup-${Date.now()}`, // Placeholder ID
        } as AnswerDTO;

        dispatch({
          type: 'SUBMIT_ANSWER_SUCCESS',
          payload: placeholderAnswer,
        });

        // Auto-advance to next slide
        if (!isLastSlide && !isAdvancingSlide) {
          setIsAdvancingSlide(true);
          setTimeout(() => {
            dispatch({ type: 'NEXT_SLIDE' });
            setTimeout(() => setIsAdvancingSlide(false), 100);
          }, 500);
        } else if (isClassExercise && isLastRealSlide) {
          // For CLASS_EXERCISE, advance to participants slide instead of completing
          setIsAdvancingSlide(true);
          setTimeout(() => {
            dispatch({ type: 'NEXT_SLIDE' });
            setTimeout(() => setIsAdvancingSlide(false), 100);
          }, 500);
        } else if (isLastSlide) {
          // Last slide - complete lesson (non-CLASS_EXERCISE)
          toast.success('Lecție completată cu succes!');
          dispatch({ type: 'COMPLETE_LESSON' });
        }
        return;
      }

      // Other errors - dispatch error action
      dispatch({
        type: 'SUBMIT_ANSWER_ERROR',
        payload: errorMsg || getErrorMessage(error, 'Eroare la salvarea răspunsului'),
      });
    }
  }, [
    currentSlide,
    state.currentAnswer,
    state.lesson,
    state.currentSlideIndex,
    validateCurrentAnswer,
    isPreview,
    isLastSlide,
    userId,
    answerMutations,
    isParticipantsSlide,
    isLastRealSlide,
    campParticipantiPePagina,
    participants,
    isClassExercise,
    isAdvancingSlide,
    isPracticalActivity,
    isClaseSlide,
    clase,
    golConfirmat,
  ]);

  const skipCurrentSlide = useCallback(() => {
    if (!config?.features.allowsMultipleAttempts && !isPreview) {
      toast.warning('Nu poți sări peste slide-uri în acest tip de lecție');
      return;
    }

    if (!isLastSlide) {
      dispatch({ type: 'NEXT_SLIDE' });
    }
  }, [config, isPreview, isLastSlide]);

  const goToSlide = useCallback(
    (index: number) => {
      const totalSlides = state.lesson?.slides?.length || 0;
      if (index < 0 || index >= totalSlides) {
        return;
      }

      // Can only jump to slides if multiple attempts allowed or preview
      if (!config?.features.allowsMultipleAttempts && !isPreview) {
        toast.warning('Nu poți naviga liber în acest tip de lecție');
        return;
      }

      dispatch({ type: 'GO_TO_SLIDE', payload: index });
    },
    [state.lesson, config, isPreview]
  );

  const nextSlide = useCallback(() => {
    if (canGoNext) {
      dispatch({ type: 'NEXT_SLIDE' });
    }
  }, [canGoNext]);

  const previousSlide = useCallback(() => {
    if (canGoPrevious) {
      dispatch({ type: 'PREVIOUS_SLIDE' });
    }
  }, [canGoPrevious]);

  const restart = useCallback(() => {
    dispatch({ type: 'RESET' });
    if (lesson) {
      dispatch({
        type: 'LOAD_SUCCESS',
        payload: { lesson, answers: [] },
      });
      dispatch({ type: 'START_LESSON' });
      if (!isPreview) {
        trackLessonStarted({ lessonType: lesson.type, lessonId: lesson.id, isPracticeMode, isResumed: false });
      }
    }
  }, [lesson, isPreview, isPracticeMode]);

  const exitLesson = useCallback(() => {
    navigate(-1); // Go back
  }, [navigate]);

  // ========================================================================
  // RETURN API
  // ========================================================================

  return {
    // State
    lesson: state.lesson,
    currentSlide,
    currentSlideIndex: state.currentSlideIndex,
    currentAnswer: state.currentAnswer,
    answers: state.answers,
    progress: state.progress,
    errors: state.errors,
    isPreview,

    // Plugin info
    currentPlugin,

    // Status flags
    isLoading: state.status === 'loading' || isLoading,
    isReady: state.status === 'ready',
    isInProgress: state.status === 'in_progress',
    isSubmitting: state.status === 'submitting' || answerMutations.isSubmitting,
    isCompleted: state.status === 'completed',
    isError: state.status === 'error',

    // Resume tracking
    isResumed: state.isResumed, // Flag indicating this is a resumed lesson
    answeredSlideIds: state.answeredSlideIds, // Set of answered slide IDs

    // Navigation flags
    isFirstSlide,
    isLastSlide,
    canGoNext,
    canGoPrevious,

    // CLASS_EXERCISE participants management
    isClassExercise,
    isParticipantsSlide,
    campParticipantiPePagina,
    participants,
    setParticipants,
    participantiIncercat,
    // SP-05a — ecranul „care clase" al activitatii practice.
    isPracticalActivity,
    isClaseSlide,
    clase,
    setClase,
    fragmenteRespinse,
    claseDejaPunctate,
    aTrimisAcum,

    // Validation
    validateCurrentAnswer,
    canSubmitCurrentAnswer,
    canSubmitLesson: () =>
      state.lesson ? canSubmitLesson(state.answers, state.lesson) : false,

    // Configuration
    config,

    // Workflow operations
    start,
    updateAnswer,
    submitCurrentAnswer,
    skipCurrentSlide,
    nextSlide,
    previousSlide,
    goToSlide,
    restart,
    exitLesson,

    // Utility
    getTotalSlides: () => state.lesson?.slides?.length || 0,
    getCompletedSlides: () => state.answers.length,
    getPercentComplete: () => state.progress.percentComplete,
    getRemainingSlides: () =>
      (state.lesson?.slides?.length || 0) - state.answers.length,
  };
}

